/**
 * Track Inquiry Page
 *
 * Replaces Login. Users enter phone (or email for existing OAuth users)
 * to verify via OTP and access their inquiries.
 * Framing: "Track Your Inquiry" — not "Login"
 */

import { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  TextField,
  Button,
  Banner,
  InlineStack,
  Box,
} from '@shopify/polaris';
import { OTPInput } from '../components/common/OTPInput';
import { useAuthStore } from '../store/authStore';
import { useUIStore } from '../store/uiStore';
import * as authService from '../services/auth.service';

type InputMode = 'phone' | 'email';
type Step = 'identifier' | 'otp';

export function TrackInquiryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login: setAuth } = useAuthStore();
  const { showToast } = useUIStore();

  const [mode, setMode] = useState<InputMode>('phone');
  const [step, setStep] = useState<Step>('identifier');
  const [identifier, setIdentifier] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/inquiries';

  const handleSendOTP = useCallback(async () => {
    if (!identifier.trim()) {
      setError(`Please enter your ${mode === 'phone' ? 'phone number' : 'email address'}`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await authService.requestOTP(identifier, mode);
      showToast(`OTP sent to your ${mode}`, 'success');
      setStep('otp');
    } catch {
      setError('Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [identifier, mode, showToast]);

  const handleVerifyOTP = useCallback(async (otp: string) => {
    setLoading(true);
    setError(null);

    try {
      const result = await authService.verifyOTP(identifier, otp, mode);
      setAuth(result.user, result.tokens);
      showToast('Verified successfully!', 'success');
      navigate(from, { replace: true });
    } catch {
      setError('Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [identifier, mode, setAuth, showToast, navigate, from]);

  const toggleMode = useCallback(() => {
    setMode((m) => (m === 'phone' ? 'email' : 'phone'));
    setIdentifier('');
    setError(null);
    setStep('identifier');
  }, []);

  return (
    <Page title="Track Your Inquiry" narrowWidth>
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="p" variant="bodyMd" tone="subdued">
                Enter your {mode === 'phone' ? 'phone number' : 'email address'} to view your inquiries, quotes, and messages.
              </Text>

              {error && (
                <Banner tone="critical" onDismiss={() => setError(null)}>
                  {error}
                </Banner>
              )}

              {step === 'identifier' ? (
                <BlockStack gap="400">
                  <TextField
                    label={mode === 'phone' ? 'Phone Number' : 'Email Address'}
                    type={mode === 'phone' ? 'tel' : 'email'}
                    value={identifier}
                    onChange={setIdentifier}
                    placeholder={mode === 'phone' ? '+91 98765 43210' : 'you@company.com'}
                    autoComplete={mode === 'phone' ? 'tel' : 'email'}
                    autoFocus
                  />

                  <Button
                    variant="primary"
                    size="large"
                    fullWidth
                    onClick={handleSendOTP}
                    loading={loading}
                  >
                    Send OTP
                  </Button>

                  <Box paddingBlockStart="200">
                    <InlineStack align="center">
                      <Button variant="plain" onClick={toggleMode}>
                        {mode === 'phone'
                          ? 'Use email instead (for existing users)'
                          : 'Use phone number instead'}
                      </Button>
                    </InlineStack>
                  </Box>
                </BlockStack>
              ) : (
                <BlockStack gap="400">
                  <Text as="p" variant="bodyMd">
                    We sent a 6-digit code to <Text as="span" fontWeight="semibold">{identifier}</Text>
                  </Text>

                  <OTPInput
                    onComplete={handleVerifyOTP}
                    loading={loading}
                  />

                  <Box paddingBlockStart="200">
                    <InlineStack align="center">
                      <Button
                        variant="plain"
                        onClick={() => {
                          setStep('identifier');
                          setError(null);
                        }}
                      >
                        Change {mode === 'phone' ? 'phone number' : 'email'}
                      </Button>
                    </InlineStack>
                  </Box>
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="200">
              <Text as="h3" variant="headingSm">
                Why track your inquiry?
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                View the status of your inquiries, see quotes from our team, accept or negotiate pricing,
                and communicate directly with us — all in one place.
              </Text>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
